import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';

// Human-readable HH:MM (ru-RU/en-US) for a frame/photo's capture timestamp.
// Shared by the dashboard proof grid + the ops-task report photos so every
// hover-timestamp reads the same. Returns '' when there's no usable timestamp.
// Pinned to Moscow time (Alexey 2026-08-04): the business operates in MSK
// regardless of which timezone a browser/viewer happens to be in — without an
// explicit timeZone, a viewer abroad (or a laptop with the wrong OS tz) sees
// times SHIFT relative to what's printed on-site/in reports, which reads as
// "the time jumps around." All frame/photo capture timestamps are MSK-relevant
// regardless of viewer location, so pin it here rather than per call site.
export function useFrameTime() {
  const { lang } = useT();
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';
  return (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' });
  };
}

// Hover-only timestamp chip for miniature photos. Positioned via `corner` so it
// can sit opposite an existing overlay (e.g. the очередь count) without
// colliding. Reveals on hover of the nearest `group` ancestor.
export function HoverTime({
  time,
  corner = 'top-left',
  always = false,
  compact = false,
}: {
  time: string;
  corner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  /** Keep the chip permanently visible (used where the timestamp is crucial,
   *  e.g. the day-report "характерные кадры" grids) instead of hover-only. */
  always?: boolean;
  /** Smaller, tucked right into the photo corner — for narrow strip photos
   *  where the regular chip covers half the frame. */
  compact?: boolean;
}) {
  if (!time) return null;
  const pos = compact
    ? {
        'top-left': 'left-0 top-0 rounded-br rounded-tl',
        'top-right': 'right-0 top-0 rounded-bl rounded-tr',
        'bottom-left': 'bottom-0 left-0 rounded-bl rounded-tr',
        'bottom-right': 'bottom-0 right-0 rounded-br rounded-tl',
      }[corner]
    : {
        'top-left': 'left-1 top-1 rounded',
        'top-right': 'right-1 top-1 rounded',
        'bottom-left': 'bottom-1 left-1 rounded',
        'bottom-right': 'bottom-1 right-1 rounded',
      }[corner];
  return (
    <span
      className={cn(
        'pointer-events-none absolute z-10 bg-black/60 font-medium leading-none text-white backdrop-blur-sm',
        compact ? 'px-0.5 py-px text-[8px]' : 'px-1 py-0.5 text-[10px]',
        always ? '' : 'opacity-0 transition-opacity duration-150 group-hover:opacity-100',
        pos,
      )}
    >
      {time}
    </span>
  );
}
